import axios from 'axios';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type {
  ShiptimeCarrierSelection,
  ShiptimeMegaShipmentProcessCreate,
  ShiptimeShipmentResponse,
} from '@shipium-corp/shipium-ts-types';

const TIMEOUT = 5000;

/**
 * Lookup Shipment by Shipium Shipment ID via authoritative API
 *
 * Handles any shipment identifier format:
 * - Shipium shipment IDs (UUIDs)
 * - Partner shipment IDs (any format)
 */
export const getShipiumShipmentById = async (shipiumShipmentId: string): Promise<ShiptimeShipmentResponse | null> => {
  try {
    const { data } = await GatewayAxiosClient.get<ShiptimeShipmentResponse>(
      `/shipment-ui-proxy/api/v1/deliveryexperience/shipment/${shipiumShipmentId}`,
      {
        params: { includeCancelled: true },
        timeout: TIMEOUT,
      },
    );

    return data || null;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw error;
  }
};

export const processShipment = async (
  request: ShiptimeMegaShipmentProcessCreate,
): Promise<ShiptimeShipmentResponse> => {
  const { data } = await GatewayAxiosClient.post<ShiptimeShipmentResponse>(
    `/shiptime-proxy/api/v1/shipment/carrierselection/label`,
    request,
  );
  return data;
};

/**
 * Runs a carrier-selection rule test.
 *
 * shiptime strips `details` from the process response unless the caller holds the internal
 * carrier-selection debugging authority (Shipium users only), and the rule-test execution trace is
 * built from `details.carrierSelection.carrierDecision`. For everyone else, backfill the decision from
 * the carrier-selection shipment-ui-proxy, which merges admin decision details for the delegated
 * partner (same data the Shipments page shows).
 */
export const runRuleTest = async (
  request: ShiptimeMegaShipmentProcessCreate,
  partnerId: string,
): Promise<ShiptimeShipmentResponse> => {
  const response = await processShipment(request);
  const carrierSelectionId = response.carrierSelection?.carrierSelectionId;
  if (response.details || !response.shipiumShipmentId || !carrierSelectionId) return response;

  try {
    const { data: carrierSelection } = await GatewayAxiosClient.get<ShiptimeCarrierSelection>(
      `/shipment-ui-proxy/api/v1/deliveryexperience/shipment/${response.shipiumShipmentId}/carrierSelection/${carrierSelectionId}`,
      { headers: { 'x-delegated-partner-id': partnerId }, timeout: TIMEOUT },
    );
    return carrierSelection?.carrierDecision?.details ? { ...response, details: { carrierSelection } } : response;
  } catch {
    return response;
  }
};
